'use client';

import { useState, useRef, useEffect } from 'react';
import { AnimationContainer } from '@/components/ui/animation-container';
import { MagicBadge } from '@/components/ui/magic-badge';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { TranslationKeys } from '../types';

// FAQ Accordion Item Component — uses CSS animation instead of framer-motion
function FaqItem({ question, answer, index }: { question: string; answer: string; index: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1, rootMargin: '50px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.4s ease-out ${index * 0.08}s, transform 0.4s ease-out ${index * 0.08}s`,
      }}
    >
      <div
        className={`bg-white border rounded-2xl overflow-hidden transition-all duration-300 ${
          isOpen ? 'border-purple-200 shadow-lg shadow-purple-100/50' : 'border-gray-200 hover:border-purple-100'
        }`}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer"
        >
          <span className="font-medium text-gray-900 pr-4">{question}</span>
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
            isOpen ? 'bg-purple-100 rotate-180' : 'bg-gray-100'
          }`}>
            <ChevronDown className={`w-5 h-5 transition-colors ${isOpen ? 'text-purple-600' : 'text-gray-500'}`} />
          </div>
        </button>
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-6 pb-5 text-gray-600 leading-relaxed">
            {answer}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FaqSection({ t, copy }: { t: TranslationKeys; copy?: import('@/lib/marketing/site-copy').SiteCopy }) {
  const { copyOr } = require('@/lib/marketing/site-copy') as typeof import('@/lib/marketing/site-copy');
  const faqItems = [
    { question: copyOr(copy, 'home.faq.q1',  t.faq1Q),  answer: copyOr(copy, 'home.faq.a1',  t.faq1A) },
    { question: copyOr(copy, 'home.faq.q2',  t.faq2Q),  answer: copyOr(copy, 'home.faq.a2',  t.faq2A) },
    { question: copyOr(copy, 'home.faq.q3',  t.faq3Q),  answer: copyOr(copy, 'home.faq.a3',  t.faq3A) },
    { question: copyOr(copy, 'home.faq.q4',  t.faq4Q),  answer: copyOr(copy, 'home.faq.a4',  t.faq4A) },
    { question: copyOr(copy, 'home.faq.q5',  t.faq5Q),  answer: copyOr(copy, 'home.faq.a5',  t.faq5A) },
    { question: copyOr(copy, 'home.faq.q6',  t.faq6Q),  answer: copyOr(copy, 'home.faq.a6',  t.faq6A) },
    { question: copyOr(copy, 'home.faq.q7',  t.faq7Q),  answer: copyOr(copy, 'home.faq.a7',  t.faq7A) },
    { question: copyOr(copy, 'home.faq.q8',  t.faq8Q),  answer: copyOr(copy, 'home.faq.a8',  t.faq8A) },
    { question: copyOr(copy, 'home.faq.q9',  t.faq9Q),  answer: copyOr(copy, 'home.faq.a9',  t.faq9A) },
    { question: copyOr(copy, 'home.faq.q10', t.faq10Q), answer: copyOr(copy, 'home.faq.a10', t.faq10A) },
  ];
  return (
    <section id="faq" className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <AnimationContainer delay={0.1}>
          <div className="flex flex-col items-center justify-center w-full mb-8">
            <MagicBadge title={t.faq} />
          </div>
        </AnimationContainer>

        <div className="max-w-4xl mx-auto space-y-4">
            {faqItems.map((faq, index) => (
              <FaqItem key={index} question={faq.question} answer={faq.answer} index={index} />
            ))}
          </div>

        <AnimationContainer delay={0.3}>
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">{t.stillQuestions}</p>
            <a
              href="mailto:hello@ezdocu.ai"
              className="inline-flex items-center justify-center rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50 px-4 h-10 text-sm font-medium no-underline transition-colors touch-manipulation cursor-pointer"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              {t.contactSupport}
            </a>
          </div>
        </AnimationContainer>
      </div>
    </section>
  );
}
